import {
  keepPreviousData,
  useInfiniteQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback } from 'react';
import { terraProxyApiClient } from '../../clients/testerClient';
import type {
  AssetsApiTypeDto,
  AssetPreviewDto,
  AssetsQueryDto,
  AssetsSearchGroup,
  AssetsSearchGroupBy,
  AssetsSearchOrderBy,
  UnverifiedAssetStatus,
} from '@terra/shared-contracts-assets';
import type { LifecycleStateFilterDto } from '@terra/shared-dto-types';
import {
  ASSET_SEARCH_SOURCE,
  type AssetSearchSource,
} from '@terra/ui-design-system';
import {
  ASSET_TYPE,
  supportsCodeGraphFilter,
  type AssetType,
  type AssetsDetectionView,
  type AssetsSecurityFilter,
  type HttpMethodVerb,
} from '../../store/tab/assetsTabScopedFilterStore';
import {
  buildVerifiedAssetsSearchBody,
  supportsAssetsSearchEndpoint,
  toLocalDayDateRangeParams,
  toAssetsQueryType,
  toPreviewOrderBy,
  type AssetsSearchRequestFilters,
} from './assets-search-request';

export const ASSETS_SEARCH_QUERY_KEY = 'assets-unified';

export interface UseAssetsSearchOptions {
  applicationId: string | null | undefined;
  typeFilter?: AssetType;
  methodsFilter?: HttpMethodVerb[];
  apiTypes?: AssetsApiTypeDto[];
  searchTerm?: string;
  securityFilter?: AssetsSecurityFilter | null;
  detectionView?: AssetsDetectionView;
  pageSize?: number;
  createdStartDate?: Date | null;
  createdEndDate?: Date | null;
  createdSince?: Date | null;
  updatedStartDate?: Date | null;
  updatedEndDate?: Date | null;
  withCodeGraph?: boolean;
  orderBy?: AssetsSearchOrderBy[];
  groupBy?: AssetsSearchGroupBy;
  domainFilters?: AssetsQueryDto['domainFilters'];
  sources?: AssetSearchSource[];
  lifecycleState?: LifecycleStateFilterDto;
  unverifiedStatuses?: UnverifiedAssetStatus[];
  enabled?: boolean;
}

type AssetsSearchSourcePage = {
  assets: AssetPreviewDto[];
  totalCount: number;
  hasMore: boolean;
  groups?: AssetsSearchGroup[];
};

export function useAssetsSearch({
  applicationId,
  typeFilter = ASSET_TYPE.ENDPOINTS,
  methodsFilter = [],
  apiTypes,
  searchTerm = '',
  securityFilter = null,
  detectionView = 'active',
  pageSize = 50,
  createdStartDate,
  createdEndDate,
  createdSince,
  updatedStartDate,
  updatedEndDate,
  withCodeGraph = false,
  orderBy,
  groupBy,
  domainFilters,
  sources = [ASSET_SEARCH_SOURCE.VERIFIED],
  lifecycleState,
  unverifiedStatuses,
  enabled = true,
}: UseAssetsSearchOptions) {
  const requestFilters: AssetsSearchRequestFilters = {
    typeFilter,
    methodsFilter,
    apiTypes,
    searchTerm,
    securityFilter,
    detectionView,
    createdStartDate,
    createdEndDate,
    createdSince,
    updatedStartDate,
    updatedEndDate,
    withCodeGraph,
    orderBy,
    groupBy,
    domainFilters,
    lifecycleState,
  };

  const buildPreviewQueryOptions = ({
    skip,
    source,
  }: {
    skip: number;
    source: AssetSearchSource;
  }): AssetsQueryDto => {
    const isUnverified = source === ASSET_SEARCH_SOURCE.UNVERIFIED;
    const previewOrderBy = toPreviewOrderBy(orderBy);
    const trimmedSearchTerm = searchTerm.trim();

    return {
      limit: pageSize,
      skip,
      type: isUnverified ? ASSET_TYPE.ENDPOINTS : toAssetsQueryType(typeFilter),
      ...(isUnverified ? { isVerified: false } : {}),
      ...(isUnverified && unverifiedStatuses && unverifiedStatuses.length > 0
        ? { unverifiedStatuses }
        : {}),
      ...(!isUnverified && domainFilters ? { domainFilters } : {}),
      ...toLocalDayDateRangeParams({
        createdStartDate,
        createdEndDate,
        createdSince,
        updatedStartDate,
        updatedEndDate,
      }),
      ...(previewOrderBy ? { orderBy: previewOrderBy } : {}),
      ...(methodsFilter.length > 0 ? { methods: methodsFilter } : {}),
      ...(trimmedSearchTerm ? { url: trimmedSearchTerm } : {}),
      ...(withCodeGraph && supportsCodeGraphFilter(typeFilter) && !isUnverified
        ? { withCodeGraph: true }
        : {}),
      ...(lifecycleState ? { lifecycleState } : {}),
    };
  };

  const fetchSourcePage = async ({
    applicationId: resolvedApplicationId,
    skip,
    source,
  }: {
    applicationId: string;
    skip: number;
    source: AssetSearchSource;
  }): Promise<AssetsSearchSourcePage> => {
    const searchBody =
      source === ASSET_SEARCH_SOURCE.VERIFIED &&
      supportsAssetsSearchEndpoint(typeFilter)
        ? buildVerifiedAssetsSearchBody({
            filters: requestFilters,
            skip,
            limit: pageSize,
          })
        : null;
    const searchResult = searchBody
      ? await terraProxyApiClient.searchApplicationAssets({
          applicationId: resolvedApplicationId,
          body: searchBody,
        })
      : undefined;
    const result = searchBody
      ? searchResult
      : await terraProxyApiClient.searchAssetsPreview(
          resolvedApplicationId,
          buildPreviewQueryOptions({ skip, source }),
        );
    const assets = result?.assets ?? [];
    const totalCount = result?.totalCount ?? 0;
    const groups = searchResult?.groups;

    return {
      assets,
      totalCount,
      hasMore: skip + assets.length < totalCount,
      ...(groups ? { groups } : {}),
    };
  };

  return useInfiniteQuery({
    queryKey: [
      ASSETS_SEARCH_QUERY_KEY,
      applicationId,
      methodsFilter,
      apiTypes,
      typeFilter,
      searchTerm,
      securityFilter,
      detectionView,
      createdStartDate,
      createdEndDate,
      createdSince,
      updatedStartDate,
      updatedEndDate,
      withCodeGraph,
      orderBy,
      groupBy,
      domainFilters,
      sources,
      lifecycleState,
      unverifiedStatuses,
    ],
    queryFn: async ({ pageParam = 0 }) => {
      if (!applicationId || sources.length === 0) {
        return { assets: [], hasMore: false, totalCount: 0, page: pageParam };
      }

      const skip = pageParam * pageSize;
      const results = await Promise.all(
        sources.map((source) =>
          fetchSourcePage({ applicationId, skip, source }),
        ),
      );
      const groups = results.flatMap((result) => result.groups ?? []);

      return {
        assets: results.flatMap((result) => result.assets),
        totalCount: results.reduce((sum, result) => sum + result.totalCount, 0),
        hasMore: results.some((result) => result.hasMore),
        page: pageParam,
        ...(groups.length > 0 ? { groups } : {}),
      };
    },
    getNextPageParam: (lastPage) => {
      return lastPage.hasMore ? (lastPage.page ?? 0) + 1 : undefined;
    },
    enabled: !!applicationId && enabled,
    refetchOnWindowFocus: false,
    staleTime: 30000,
    placeholderData: (previousData, previousQuery) => {
      const previousApplicationId = previousQuery?.queryKey[1];

      if (previousApplicationId !== applicationId) {
        return undefined;
      }

      return keepPreviousData(previousData);
    },
    initialPageParam: 0,
  });
}

export const useInvalidateAssetsSearch = () => {
  const queryClient = useQueryClient();

  return useCallback(
    async ({ applicationId }: { applicationId: string }) => {
      await queryClient.invalidateQueries({
        queryKey: [ASSETS_SEARCH_QUERY_KEY, applicationId],
        exact: false,
      });
    },
    [queryClient],
  );
};
